import { createFileRoute, Link } from "@tanstack/react-router";
import { useContext, useMemo, useState } from "react";
import {
  AppError,
  Button,
  EmptyState,
  Loader,
  Menu,
  MenuListItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
  toast,
} from "@bfc-software/pallet";
import { Dialog, Modal, ModalOverlay } from "react-aria-components";
import { TFunction } from "i18next";
import { AuthContext, AuthUtils, logger } from "@bfc-software/app-toolbox";
import {
  useTranslation,
  TRANSLATION_NAMESPACE,
} from "@bfc-software/bfc-web-ui/i18n";
import { apiClient } from "@bfc-software/bfc-web-ui/lib/webhooks-api-client";
import palletOverrides from "@bfc-software/bfc-web-ui/pallet-overrides.module.css";
import modalStyles from "@bfc-software/pallet/components/structure/modal.module.css";

import styles from "./index.module.css";

export const Route = createFileRoute(
  "/$lang/_layout/orgs/$org/_layout/internal/whs/$location/_layout/webhooks/endpoints/",
)({
  staticData: {
    crumb: (_: unknown, t: TFunction) => ({
      label: t("page.webhookEndpoints.crumb"),
    }),
  },
  component: RouteComponent,
});

type DeleteTarget = { id: string; url: string };

export function RouteComponent() {
  const { lang, org, location } = Route.useParams();
  const { state: authState } = useContext(AuthContext);
  const { t } = useTranslation({
    lang,
    namespace: TRANSLATION_NAMESPACE.warehouse,
  });

  const client = useMemo(() => apiClient(), []);

  const orgId = AuthUtils.getOrgIdByStub(authState, org) ?? org;
  const warehouseId = AuthUtils.getWarehouseId(authState, orgId, location);

  const enabled = typeof orgId === "string" && typeof warehouseId === "string";

  const query = client.useQuery(
    "get",
    "/{orgId}/warehouses/{warehouseId}/endpoints",
    {
      params: {
        path: { orgId: orgId!, warehouseId: warehouseId! },
      },
    },
    { enabled },
  );

  const deleteMutation = client.useMutation(
    "delete",
    "/{orgId}/warehouses/{warehouseId}/endpoints/{endpointId}",
  );

  const [deleteTarget, setDeleteTarget] = useState<DeleteTarget | undefined>();

  const endpoints = query.data?.data ?? [];

  const closeConfirm = () => {
    if (deleteMutation.isPending) return;
    setDeleteTarget(undefined);
  };

  const onConfirmDelete = () => {
    if (!deleteTarget || !enabled || deleteMutation.isPending) return;

    const target = deleteTarget;
    deleteMutation
      .mutateAsync({
        params: {
          path: {
            orgId: orgId!,
            warehouseId: warehouseId!,
            endpointId: target.id,
          },
        },
      })
      .then(() => {
        logger.info("Deleted webhook endpoint", { id: target.id });
        toast.success(t("page.webhookEndpoints.deleteConfirm.successToast"));
        setDeleteTarget(undefined);
        query.refetch();
      })
      .catch((error: unknown) => {
        logger.warn("Failed to delete webhook endpoint", { error });
        const fallback = t("page.webhookEndpoints.deleteConfirm.errorToast");
        toast.error(error instanceof Error ? error.message : fallback);
      });
  };

  return (
    <div className={palletOverrides.basicPageContent}>
      <div className={palletOverrides.basicPageActionArea}>
        <div className={palletOverrides.basicPageActionAreaLeft}>
          <h1>{t("page.webhookEndpoints.title")}</h1>
        </div>
        <div className={palletOverrides.basicPageActionAreaRight}>
          <Link
            to="/$lang/orgs/$org/internal/whs/$location/webhooks/endpoints/new"
            params={{ lang, org, location }}
            className={palletOverrides.linkButton}
          >
            {t("page.webhookEndpoints.newButton")}
          </Link>
        </div>
      </div>
      {query.isLoading || !enabled ? (
        <Loader type="page" />
      ) : query.isError ? (
        <AppError
          description={t("page.webhookEndpoints.failed")}
          resolution={t("page.webhookEndpoints.failureSupport")}
        />
      ) : (
        <Table
          aria-label={t("page.webhookEndpoints.table.ariaLabel")}
          className={
            endpoints.length < 1 ? palletOverrides.fullHeightTable : ""
          }
          isResizable={false}
        >
          <TableHead>
            <TableHeaderCell isRowHeader={true}>
              {t("page.webhookEndpoints.table.url")}
            </TableHeaderCell>
            <TableHeaderCell>
              {t("page.webhookEndpoints.table.description")}
            </TableHeaderCell>
            <TableHeaderCell>
              {t("page.webhookEndpoints.table.status")}
            </TableHeaderCell>
            <TableHeaderCell>
              {t("page.webhookEndpoints.table.created")}
            </TableHeaderCell>
            <TableHeaderCell>
              <span aria-label={t("page.webhookEndpoints.table.actions")} />
            </TableHeaderCell>
          </TableHead>
          <TableBody>
            {endpoints.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5}>
                  <EmptyState heading={t("page.webhookEndpoints.empty")} />
                </TableCell>
              </TableRow>
            ) : (
              endpoints.map((endpoint) => (
                <TableRow key={endpoint.id} data-test-role="endpoint-row">
                  <TableCell>
                    <span data-test-role="endpoint-url">{endpoint.url}</span>
                  </TableCell>
                  <TableCell>{endpoint.description}</TableCell>
                  <TableCell>
                    <Link
                      data-test-role="endpoint-status"
                      to="/$lang/orgs/$org/internal/whs/$location/webhooks/endpoints/$endpoint/deliveries"
                      params={{ lang, org, location, endpoint: endpoint.id }}
                    >
                      {endpoint.status}
                    </Link>
                  </TableCell>
                  <TableCell>{endpoint.created_at}</TableCell>
                  <TableCell className={styles.actionsCell}>
                    <Menu
                      trigger={{
                        variant: "tertiary",
                        icon: { type: "menu" },
                        "aria-label": t(
                          "page.webhookEndpoints.rowMenu.ariaLabel",
                        ),
                      }}
                    >
                      <MenuListItem
                        onAction={() =>
                          setDeleteTarget({
                            id: endpoint.id,
                            url: endpoint.url,
                          })
                        }
                      >
                        {t("page.webhookEndpoints.rowMenu.delete")}
                      </MenuListItem>
                    </Menu>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      )}

      <ModalOverlay
        className={modalStyles.modalOverlay}
        isDismissable={!deleteMutation.isPending}
        isOpen={deleteTarget !== undefined}
        onOpenChange={(open) => {
          if (!open) closeConfirm();
        }}
      >
        <Modal className={modalStyles.modal}>
          <Dialog
            className={modalStyles.dialog}
            data-test-role="delete-endpoint-confirm"
          >
            <div className={styles.deleteConfirm}>
              <h2>{t("page.webhookEndpoints.deleteConfirm.title")}</h2>
              <p className={styles.deleteConfirmBody}>
                {t("page.webhookEndpoints.deleteConfirm.body")}
              </p>
              {deleteTarget ? (
                <p
                  className={styles.deleteConfirmTarget}
                  data-test-role="delete-endpoint-target-url"
                >
                  {deleteTarget.url}
                </p>
              ) : null}
            </div>
            <div className={styles.modalActions}>
              <Button
                text={t("page.webhookEndpoints.deleteConfirm.cancel")}
                variant="tertiary"
                onPress={closeConfirm}
                isDisabled={deleteMutation.isPending}
              />
              <Button
                text={t("page.webhookEndpoints.deleteConfirm.confirm")}
                variant="danger"
                onPress={onConfirmDelete}
                isDisabled={deleteMutation.isPending}
              />
            </div>
          </Dialog>
        </Modal>
      </ModalOverlay>
    </div>
  );
}
